import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { orderService } from '../services/orderService';

interface CheckoutFormData {
    delivery_address: string;
    delivery_city: string;
    delivery_postal_code: string;
    delivery_phone: string;
    delivery_notes: string;
    preferred_delivery_date: string;
    preferred_delivery_time: 'morning' | 'afternoon' | 'evening';
}

const CheckoutPage: React.FC = () => {
    const { user } = useAuth();
    const { items, totalAmount, clearCart } = useCart();
    const navigate = useNavigate();

    const [formData, setFormData] = useState<CheckoutFormData>({
        delivery_address: '',
        delivery_city: '',
        delivery_postal_code: '',
        delivery_phone: '',
        delivery_notes: '',
        preferred_delivery_date: '',
        preferred_delivery_time: 'morning'
    });

    const [loading, setLoading] = useState(false);

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    const getItemPrice = (item: any) => {
        return typeof item.product?.price === 'string'
            ? parseFloat(item.product.price)
            : item.product?.price || 0;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Create order object
            const orderData: Parameters<typeof orderService.createOrder>[0] = {
                items: items.map(item => ({
                    product_id: item.product_id,
                    quantity: item.quantity,
                    unit_price: getItemPrice(item),
                    product_name: item.product?.name || ''
                })),
                total_amount: totalAmount,
                delivery_address: formData.delivery_address,
                delivery_city: formData.delivery_city,
                delivery_postal_code: formData.delivery_postal_code,
                delivery_phone: formData.delivery_phone,
                delivery_notes: formData.delivery_notes,
                preferred_delivery_date: formData.preferred_delivery_date,
                preferred_delivery_time: formData.preferred_delivery_time,
                payment_method: 'cash' as const
            };

            // Send order to backend API
            const response = await orderService.createOrder(orderData);

            // Clear cart and redirect
            clearCart();
            alert(`Commande ${response.order.order_number} passée avec succès! Vous recevrez une confirmation par email.`);
            navigate(`/orders/${response.order.id}`);

        } catch (error: any) {
            console.error('Erreur lors de la commande:', error);
            const errorMessage = error.response?.data?.error || 'Erreur lors de la commande. Veuillez réessayer.';
            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="min-h-screen bg-eldai-gray-light">
                <div className="container mx-auto px-4 py-8">
                    <div className="text-center py-16">
                        <h1 className="text-3xl font-bold text-eldai-blue mb-4">
                            Votre panier est vide
                        </h1>
                        <p className="text-eldai-gray mb-8">
                            Ajoutez des produits à votre panier avant de passer commande
                        </p>
                        <button
                            onClick={() => navigate('/catalog')}
                            className="bg-eldai-blue text-white px-8 py-3 rounded-lg font-semibold hover:bg-eldai-light-blue transition-colors"
                        >
                            Voir le catalogue
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-eldai-gray-light">
            <div className="container mx-auto px-4 py-8">
                <h1 className="text-3xl font-bold text-eldai-blue mb-8">
                    Finaliser la commande
                </h1>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Order Form */}
                    <div className="lg:col-span-2">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Customer Info */}
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h2 className="text-xl font-bold text-eldai-blue mb-4">
                                    Informations client
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-eldai-blue mb-1">
                                            Nom
                                        </label>
                                        <input
                                            type="text"
                                            value={`${user?.first_name || ''} ${user?.last_name || ''}`}
                                            disabled
                                            className="w-full px-4 py-2 border border-eldai-gray rounded-lg bg-eldai-gray-light"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-eldai-blue mb-1">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            value={user?.email || ''}
                                            disabled
                                            className="w-full px-4 py-2 border border-eldai-gray rounded-lg bg-eldai-gray-light"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Delivery Address */}
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h2 className="text-xl font-bold text-eldai-blue mb-4">
                                    Adresse de livraison
                                </h2>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-eldai-blue mb-1">
                                            Adresse *
                                        </label>
                                        <input
                                            type="text"
                                            name="delivery_address"
                                            value={formData.delivery_address}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-blue"
                                            placeholder="123 rue de la République"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-eldai-blue mb-1">
                                                Ville *
                                            </label>
                                            <input
                                                type="text"
                                                name="delivery_city"
                                                value={formData.delivery_city}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-4 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-blue"
                                                placeholder="Paris"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-eldai-blue mb-1">
                                                Code postal *
                                            </label>
                                            <input
                                                type="text"
                                                name="delivery_postal_code"
                                                value={formData.delivery_postal_code}
                                                onChange={handleInputChange}
                                                required
                                                pattern="[0-9]{5}"
                                                className="w-full px-4 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-blue"
                                                placeholder="75001"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-eldai-blue mb-1">
                                            Téléphone de livraison *
                                        </label>
                                        <input
                                            type="tel"
                                            name="delivery_phone"
                                            value={formData.delivery_phone}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-blue"
                                            placeholder="06 12 34 56 78"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Delivery Options */}
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h2 className="text-xl font-bold text-eldai-blue mb-4">
                                    Options de livraison
                                </h2>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-eldai-blue mb-1">
                                            Date de livraison préférée
                                        </label>
                                        <input
                                            type="date"
                                            name="preferred_delivery_date"
                                            value={formData.preferred_delivery_date}
                                            onChange={handleInputChange}
                                            min={new Date().toISOString().split('T')[0]}
                                            className="w-full px-4 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-blue"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-eldai-blue mb-1">
                                            Créneau horaire préféré
                                        </label>
                                        <select
                                            name="preferred_delivery_time"
                                            value={formData.preferred_delivery_time}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-blue"
                                        >
                                            <option value="morning">Matin (8h-12h)</option>
                                            <option value="afternoon">Après-midi (12h-17h)</option>
                                            <option value="evening">Soir (17h-20h)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-eldai-blue mb-1">
                                            Instructions de livraison
                                        </label>
                                        <textarea
                                            name="delivery_notes"
                                            value={formData.delivery_notes}
                                            onChange={handleInputChange}
                                            rows={3}
                                            className="w-full px-4 py-2 border border-eldai-gray rounded-lg focus:outline-none focus:ring-2 focus:ring-eldai-blue"
                                            placeholder="Code d'accès, étage, instructions spéciales..."
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Payment Method */}
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h2 className="text-xl font-bold text-eldai-blue mb-4">
                                    Mode de paiement
                                </h2>
                                <div className="bg-eldai-yellow p-4 rounded-lg">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-2xl">💳</span>
                                        <div>
                                            <h3 className="font-semibold text-eldai-blue">
                                                Paiement à la livraison
                                            </h3>
                                            <p className="text-sm text-eldai-gray">
                                                Vous payez en espèces ou par carte bancaire au moment de la livraison
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Order Summary */}
                    <div className="bg-white rounded-lg shadow-md p-6 h-fit">
                        <h2 className="text-xl font-bold text-eldai-blue mb-4">
                            Récapitulatif de commande
                        </h2>

                        {/* Order Items */}
                        <div className="space-y-3 mb-6">
                            {items.map((item) => (
                                <div key={item.id} className="flex items-center justify-between text-sm">
                                    <div className="flex-1">
                                        <span className="font-medium">{item.product?.name}</span>
                                        <span className="text-eldai-gray ml-2">x{item.quantity}</span>
                                    </div>
                                    <span className="font-semibold">
                                        {formatPrice(getItemPrice(item) * item.quantity)}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Totals */}
                        <div className="space-y-2 border-t pt-4 mb-6">
                            <div className="flex justify-between">
                                <span>Sous-total</span>
                                <span>{formatPrice(totalAmount)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Livraison</span>
                                <span className="text-green-600">Gratuite</span>
                            </div>
                            <div className="flex justify-between text-lg font-bold border-t pt-2">
                                <span>Total</span>
                                <span className="text-eldai-blue">{formatPrice(totalAmount)}</span>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="w-full bg-eldai-yellow text-eldai-blue py-3 px-6 rounded-lg font-semibold hover:bg-eldai-blue hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Commande en cours...' : 'Confirmer la commande'}
                        </button>

                        <p className="text-xs text-eldai-gray mt-4 text-center">
                            En confirmant cette commande, vous acceptez nos conditions générales de vente
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;
